import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Logo } from "@/components/Logo";
import Organizations from "@/pages/Organizations";
import {
  Video, LayoutGrid, FolderKanban, Radio, KeyRound, BarChart3, Users, BookOpen, Settings as Cog,
  ChevronsUpDown, LogOut, Check, Gauge,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard/organizations", label: "Organizations", icon: Users },
  { to: "/dashboard", label: "Overview", icon: LayoutGrid, end: true },
  { to: "/dashboard/projects", label: "Projects", icon: FolderKanban },
  { to: "/dashboard/calls", label: "Appels", icon: Radio },
  { to: "/dashboard/meetings", label: "Réunions", icon: Users },
  { to: "/dashboard/workspaces", label: "Espaces de travail", icon: FolderKanban },
  { to: "/dashboard/rooms", label: "Rooms", icon: Radio },
  { to: "/dashboard/api-keys", label: "API Keys", icon: KeyRound },
  { to: "/dashboard/usage", label: "Usage", icon: BarChart3 },
  { to: "/dashboard/plan", label: "Plan", icon: Gauge },
  { to: "/dashboard/recordings", label: "Enregistrements", icon: Video },
  { to: "/dashboard/delegations", label: "Délégations", icon: Users },
  { to: "/dashboard/members", label: "Members", icon: Users },
  { to: "/dashboard/quickstart", label: "Quick Start", icon: BookOpen },
  { to: "/dashboard/branding", label: "Marque blanche", icon: Cog },
  { to: "/dashboard/settings", label: "Settings", icon: Cog },
];

function Switcher({ label, items, value, onSelect, testId }) {
  const current = items.find((i) => i.id === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={testId}
          className="flex w-full items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-left transition-colors hover:border-zinc-700"
        >
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
            <div className="truncate text-sm text-zinc-100">{current?.name || "—"}</div>
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-zinc-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 border-zinc-800 bg-zinc-950" align="start">
        <DropdownMenuLabel className="text-zinc-500">{label}</DropdownMenuLabel>
        {items.length === 0 && <div className="px-2 py-1.5 text-sm text-zinc-500">None yet</div>}
        {items.map((i) => (
          <DropdownMenuItem
            key={i.id}
            data-testid={`${testId}-item-${i.id}`}
            onClick={() => onSelect(i.id)}
            className="cursor-pointer text-zinc-200 focus:bg-zinc-800"
          >
            <span className="truncate">{i.name}</span>
            {i.id === value && <Check className="ml-auto h-4 w-4 text-emerald-400" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { orgs, projects, orgId, projectId, selectOrg, selectProject, loading, error, currentOrg } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 z-30 flex h-screen w-64 flex-col border-r border-zinc-800 bg-zinc-950">
        <div className="flex h-14 items-center border-b border-zinc-800 px-4">
          <Logo />
        </div>
        <div className="space-y-2 border-b border-zinc-800 p-3">
          <Switcher label="Organization" items={orgs} value={orgId} onSelect={(id) => { if (location.pathname.startsWith("/dashboard/rooms/")) navigate("/dashboard/rooms"); selectOrg(id); }} testId="org-switcher" />
          <Switcher label="Project" items={projects} value={projectId} onSelect={(id) => { if (location.pathname.startsWith("/dashboard/rooms/")) navigate("/dashboard/rooms"); selectProject(id); }} testId="project-switcher" />
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
          {user?.role === "admin" && <NavLink to="/admin" className="block rounded-md px-3 py-2 text-sm text-emerald-400">Administration plateforme</NavLink>}
          {NAV.filter((n) => currentOrg?.role !== "manager" || ["/dashboard/calls", "/dashboard/meetings", "/dashboard/workspaces", "/dashboard/recordings", "/dashboard/organizations", "/dashboard/settings"].includes(n.to)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive ? "bg-zinc-800 text-zinc-50" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-800 p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu"
                className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-zinc-900"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-800 text-sm font-medium text-zinc-100">
                  {(user?.name || user?.email || "?")[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-zinc-100">{user?.name}</div>
                  <div className="truncate text-xs text-zinc-500">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-52 border-zinc-800 bg-zinc-950" align="end">
              <DropdownMenuLabel className="text-zinc-400">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem
                data-testid="logout-button"
                onClick={async () => { await logout(); navigate("/login"); }}
                className="cursor-pointer text-red-400 focus:bg-zinc-800 focus:text-red-400"
              >
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main */}
      <main className="ml-64 flex-1">
        <div className="mx-auto max-w-6xl px-8 py-8">
          {loading ? <p role="status">Chargement de l’espace…</p> : error ? (
            <div className="space-y-4"><p role="alert">Impossible de charger l’espace.</p>
              {error.response?.headers?.["x-sso-organization"] ? <a className="underline" href={`/login?org_id=${encodeURIComponent(orgId)}`}>Se connecter avec le SSO de cette organisation</a> : <p>Veuillez recharger la page ou choisir une autre organisation.</p>}
              <Outlet key={`${orgId}:${projectId}`} />
            </div>
          ) : orgs.length === 0 && location.pathname !== "/dashboard/settings" ? <Organizations /> : <Outlet key={`${orgId}:${projectId}`} />}
        </div>
      </main>
    </div>
  );
}
